import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./BpsLog.scss";
import logo from "../../assets/imgs/sysb_logo.svg";
import DownloadBpsLogModal from "./components/DownloadBpsLogModal";
import SettingsModal from "./components/SettingsModal";
import BpsLogMap from "./components/BpsLogMap";
import BpsLogVideo from "./components/BpsLogVideo";
import { BpsLogNowPanel } from "./components/BpsLogNowPanel";
import { BpsLogHeader } from "./components/BpsLogHeader";
import { BitrateChartCanvas } from "./components/BitrateChartCanvas";
import { useBpsLogChartZoom } from "./hooks/useBpsLogChartZoom";
import { useBpsLogData } from "./hooks/useBpsLogData";
import { useBpsLogMetadata } from "./hooks/useBpsLogMetadata";
import { useBpsLogChartSelectionSync } from "./hooks/useBpsLogChartSelectionSync";
import { useBpsLogBitrateCharts } from "./hooks/useBpsLogBitrateCharts";
import { useBpsLogSettingsState } from "./hooks/useBpsLogSettingsState";
import { useRealtimeAxisEnd } from "./hooks/useRealtimeAxisEnd";
import { useRealtimeNowMs } from "./hooks/useRealtimeNowMs";
import { registerBpsLogChartJs } from "./utils/chartjs";
import { BpsLogPoint, ChartPoint } from "./utils/types";
import type { ChartConfig, MapActualBitrateColoring } from "./utils/settings";
import {
  formatTimeLabel,
  parseDateTimeLocal,
  toDateTimeLocalValue,
} from "./utils/datetime";
import {
  buildRealtimeChartPoints,
  trimToRealtimeWindow,
} from "./utils/realtime";
import { buildPeriodChartPoints } from "./utils/period";
import { getRealtimeAxisEndMs } from "./utils/realtimeAxis";
import {
  downsampleBpsLogPointsForMap,
  downsampleChartPoints,
  pickRepresentativeBpsLogIndicesForRangeLikeApiCap,
} from "./utils/downsample";
import { formatDurationJa } from "./utils/duration";
import { getSeriesY } from "./utils/series";
import {
  DEFAULT_DELAY_OFFSET_MS,
  DEFAULT_FOLLOW_MAP,
  DEFAULT_MAP_TILE_LAYER,
  DEFAULT_SHOW_VIDEO,
  MAX_CHART_POINTS,
  MAX_POINTS_FOR_UI,
  REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
} from "./constants";

const DEFAULT_DISABLE_CHART_DOWNSAMPLE = false;
const DEFAULT_DISABLE_MAP_DOWNSAMPLE = false;
const DEFAULT_MAP_ACTUAL_THRESHOLD_LOW_KBPS = 700;
const DEFAULT_MAP_ACTUAL_THRESHOLD_MID_KBPS = 1300;

const DEFAULT_MAP_ACTUAL_BITRATE_COLORING: MapActualBitrateColoring = {
  thresholdLowKbps: DEFAULT_MAP_ACTUAL_THRESHOLD_LOW_KBPS,
  thresholdMidKbps: DEFAULT_MAP_ACTUAL_THRESHOLD_MID_KBPS,
  lowColor: "#ff4d47",
  midColor: "#ffec3a",
  highColor: "#00b900",
};

const DEFAULT_CHARTS: ChartConfig[] = [
  { key: "l1", name: "L1 Bitrate", color: "#1976d2" },
  { key: "l2", name: "L2 Bitrate", color: "#e65100" },
  { key: "l3", name: "L3 Bitrate", color: "#cccccc" },
  { key: "actual", name: "Actual Bitrate", color: "#43a047" },
];

const QUICK_END_OFFSET_MINUTES_OPTIONS = [
  0, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240,
] as const;

registerBpsLogChartJs();

// bpslog画面（ヘッダー/チャート/地図/動画/設定/ダウンロード）全体を統括する。
const BpsLog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRealtime, setIsRealtime] = useState(true);
  const [realtimeWindowMinutes, setRealtimeWindowMinutes] =
    useState<number>(10);
  const [realtimeWindowMinutesForDisplay, setRealtimeWindowMinutesForDisplay] =
    useState<number>(10);

  const [appliedFetchNonce, setAppliedFetchNonce] = useState(0);

  const {
    baseId,
    baseName,
    basePoint,
    coursePoints,
    vehicleId,
    setVehicleId,
    vehicleOptions,
    vehicleSuidById,
  } = useBpsLogMetadata({ navigate });

  const selectedZaoSuid: string | null = useMemo(() => {
    if (vehicleId === null) return null;
    const suid = vehicleSuidById[vehicleId];
    return typeof suid === "string" && suid.trim().length ? suid : null;
  }, [vehicleId, vehicleSuidById]);

  const defaultEnd = new Date();
  const defaultStart = new Date(defaultEnd.getTime() - 10 * 60 * 1000); // 初期表示の開始時刻（現在-10分）
  const initialStartInput = toDateTimeLocalValue(defaultStart);
  const initialEndInput = toDateTimeLocalValue(defaultEnd);
  const [startInput, setStartInput] = useState(initialStartInput);
  const [endInput, setEndInput] = useState(initialEndInput);
  const [appliedStartInput, setAppliedStartInput] = useState(initialStartInput);
  const [appliedEndInput, setAppliedEndInput] = useState(initialEndInput);
  const appliedRangeRef = useRef<{ start: string; end: string }>({
    start: initialStartInput,
    end: initialEndInput,
  });

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadInitialVehicleId, setDownloadInitialVehicleId] = useState<
    number | null
  >(null);
  const [downloadInitialStart, setDownloadInitialStart] = useState("");
  const [downloadInitialEnd, setDownloadInitialEnd] = useState("");

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const {
    delayOffsetMs,
    chartConfigs,
    followMap,
    showVideo,
    disableUiCap,
    disableChartDownsample,
    disableMapDownsample,
    mapTileLayer,
    mapActualBitrateColoring,
    applySettings,
  } = useBpsLogSettingsState({
    defaultDelayOffsetMs: DEFAULT_DELAY_OFFSET_MS,
    defaultCharts: DEFAULT_CHARTS,
    defaultFollowMap: DEFAULT_FOLLOW_MAP,
    defaultShowVideo: DEFAULT_SHOW_VIDEO,
    defaultDisableChartDownsample: DEFAULT_DISABLE_CHART_DOWNSAMPLE,
    defaultDisableMapDownsample: DEFAULT_DISABLE_MAP_DOWNSAMPLE,
    defaultMapTileLayer: DEFAULT_MAP_TILE_LAYER,
    defaultMapActualBitrateColoring: DEFAULT_MAP_ACTUAL_BITRATE_COLORING,
  });

  // ダウンロードモーダルを開き、現在の表示範囲を初期値として渡す。
  const openDownloadModal = () => {
    const now = new Date();
    const realtimeStart = new Date(
      now.getTime() - realtimeWindowMinutes * 60_000,
    );
    setDownloadInitialVehicleId(vehicleId);
    setDownloadInitialStart(
      isRealtime ? toDateTimeLocalValue(realtimeStart) : appliedStartInput,
    );
    setDownloadInitialEnd(
      isRealtime ? toDateTimeLocalValue(now) : appliedEndInput,
    );
    setIsDownloadModalOpen(true);
  };

  const {
    bpslogData,
    isRangeLoading: isBpslogRangeLoading,
    cancelRequests: cancelBpsLogRequests,
    rollbackPeriodRangeToLastSuccessful,
  } = useBpsLogData({
    vehicleId,
    isRealtime,
    appliedStartInput,
    appliedEndInput,
    appliedFetchNonce,
    realtimeWindowMinutes,
    delayOffsetMs,
    realtimeRightEdgeFallbackLagMs: REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
    maxPointsForUi: MAX_POINTS_FOR_UI,
    disableUiCap,
    initialStartInput,
    initialEndInput,
    setStartInput,
    setEndInput,
    setAppliedStartInput,
    setAppliedEndInput,
  });

  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [forceLatest, setForceLatest] = useState<boolean>(true);
  const forceLatestRef = useRef(forceLatest);
  const pendingHoverIdxRef = useRef<number | null>(null);
  const hoverRafRef = useRef<number | null>(null);
  const suppressHoverUntilMsRef = useRef<number>(0);

  const [isPeriodRenderBusy, setIsPeriodRenderBusy] = useState(false);
  const clearPeriodBusyRaf1Ref = useRef<number | null>(null);
  const clearPeriodBusyRaf2Ref = useRef<number | null>(null);
  const clearPeriodBusyTimerRef = useRef<number | null>(null);

  const periodFetchKey = useMemo(() => {
    if (isRealtime) return null;
    if (vehicleId === null) return null;
    return [
      vehicleId,
      appliedStartInput,
      appliedEndInput,
      appliedFetchNonce,
      delayOffsetMs,
      disableUiCap ? 1 : 0,
    ].join("|");
  }, [
    isRealtime,
    vehicleId,
    appliedStartInput,
    appliedEndInput,
    appliedFetchNonce,
    delayOffsetMs,
    disableUiCap,
  ]);

  const leftStackRef = useRef<HTMLDivElement | null>(null);
  const chartRefs = useRef<Array<any>>([]);
  const isRealtimeRef = useRef(isRealtime);
  const stickyRealtimeAxisEndMsRef = useRef<number | null>(null);
  const resetRealtimeAxisTrackingRef = useRef<null | (() => void)>(null);
  const chartPointsRef = useRef<ChartPoint[]>([]);
  const bpslogDataForDisplayRef = useRef<BpsLogPoint[]>([]);

  useEffect(() => {
    forceLatestRef.current = forceLatest;
  }, [forceLatest]);

  // 監視モード（リアルタイム/期間指定）を切り替える。
  const setMonitorMode = useCallback((nextIsRealtime: boolean) => {
    if (nextIsRealtime) {
      if (typeof resetRealtimeAxisTrackingRef.current === "function") {
        resetRealtimeAxisTrackingRef.current();
      } else {
        stickyRealtimeAxisEndMsRef.current = null;
      }
    }
    setIsRealtime(nextIsRealtime);
    setForceLatest(nextIsRealtime);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverRafRef.current !== null) {
        window.cancelAnimationFrame(hoverRafRef.current);
        hoverRafRef.current = null;
      }
    };
  }, []);

  const realtimeNowMs = useRealtimeNowMs(isRealtime);

  const consumedNavStateRef = useRef(false);

  // Mapページ等からの遷移state（車両/期間）を取り込む。
  useEffect(() => {
    if (consumedNavStateRef.current) return;
    const raw = (location as any)?.state as any;
    if (typeof raw !== "object" || raw === null) return;

    const start = typeof raw.startInput === "string" ? raw.startInput : null;
    const end = typeof raw.endInput === "string" ? raw.endInput : null;
    if (!start || !end) return;

    const sAt = parseDateTimeLocal(start);
    const eAt = parseDateTimeLocal(end);
    if (!sAt || !eAt) return;

    consumedNavStateRef.current = true;

    // Mapページは期間指定なので、ログ側も期間指定に揃える。
    setMonitorMode(false);
    setStartInput(start);
    setEndInput(end);
    setAppliedStartInput(start);
    setAppliedEndInput(end);
  }, [
    location,
    setMonitorMode,
    setStartInput,
    setEndInput,
    setAppliedStartInput,
    setAppliedEndInput,
  ]);

  const pendingNavVehicleIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (pendingNavVehicleIdRef.current !== null) return;
    const raw = (location as any)?.state as any;
    const v = raw?.vehicleId;
    if (typeof v === "number" && Number.isFinite(v)) {
      pendingNavVehicleIdRef.current = v;
    }
  }, [location]);

  useEffect(() => {
    const target = pendingNavVehicleIdRef.current;
    if (target === null) return;
    if (vehicleId === target) {
      pendingNavVehicleIdRef.current = null;
      return;
    }
    const exists = vehicleOptions.some((o) => o?.value === target);
    if (!exists) return;
    setVehicleId(target);
    pendingNavVehicleIdRef.current = null;
  }, [vehicleId, vehicleOptions, setVehicleId]);

  // リアルタイムの表示幅は、選択値へ即時追従する。
  useEffect(() => {
    setRealtimeWindowMinutesForDisplay(realtimeWindowMinutes);
  }, [realtimeWindowMinutes]);

  const { realtimeAxisEndInfo, realtimeAxisEndMs, resetRealtimeAxisTracking } =
    useRealtimeAxisEnd({
      isRealtime,
      bpslogData,
      realtimeNowMs,
      realtimeRightEdgeFallbackLagMs: REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
      stickyRealtimeAxisEndMsRef,
      stallAfterMs: 3000,
    });

  resetRealtimeAxisTrackingRef.current = resetRealtimeAxisTracking;

  const bpslogDataForDisplay = useMemo(() => {
    if (!isRealtime) return bpslogData;
    const endMs =
      realtimeAxisEndMs ??
      realtimeAxisEndInfo?.endMs ??
      getRealtimeAxisEndMs(
        bpslogData,
        realtimeNowMs,
        REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
        stickyRealtimeAxisEndMsRef.current,
      ).endMs;
    return trimToRealtimeWindow(
      bpslogData,
      endMs,
      realtimeWindowMinutesForDisplay,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bpslogData,
    isRealtime,
    realtimeWindowMinutesForDisplay,
    realtimeNowMs,
    realtimeAxisEndMs,
  ]);

  const bpslogDataForMap = useMemo(() => {
    if (disableMapDownsample) return bpslogDataForDisplay;
    return downsampleBpsLogPointsForMap(bpslogDataForDisplay, MAX_CHART_POINTS);
  }, [bpslogDataForDisplay, disableMapDownsample]);

  useEffect(() => {
    isRealtimeRef.current = isRealtime;
  }, [isRealtime]);

  useEffect(() => {
    appliedRangeRef.current = {
      start: appliedStartInput,
      end: appliedEndInput,
    };
  }, [appliedStartInput, appliedEndInput]);

  const canApplyPeriodRange = useMemo(() => {
    if (isRealtime) return false;
    const startAt = parseDateTimeLocal(startInput);
    const endAt = parseDateTimeLocal(endInput);
    if (!startAt || !endAt) return false;
    return endAt.getTime() >= startAt.getTime();
  }, [isRealtime, startInput, endInput]);

  // 期間指定の入力値を適用して再取得する（同一範囲ならnonceで強制再取得）。
  const applyPeriodRange = () => {
    if (!canApplyPeriodRange) return;

    const startAt = parseDateTimeLocal(startInput);
    const endAt = parseDateTimeLocal(endInput);
    if (!startAt || !endAt) return;
    if (endAt.getTime() < startAt.getTime()) {
      window.alert("終了時刻を開始時刻以降に設定してください。");
      return;
    }

    // 取得ステータスの表示を即座に更新する（hook側のloading伝播待ちによる一瞬の「完了」表示を防ぐ）。
    setIsPeriodRenderBusy(true);

    const isSameAsApplied =
      startInput === appliedStartInput && endInput === appliedEndInput;
    if (isSameAsApplied) {
      setAppliedFetchNonce((v) => v + 1);
      return;
    }

    setAppliedStartInput(startInput);
    setAppliedEndInput(endInput);
  };

  const cancelPeriodRange = () => {
    if (isRealtime) return;
    cancelBpsLogRequests();
    rollbackPeriodRangeToLastSuccessful();

    if (clearPeriodBusyRaf1Ref.current !== null) {
      window.cancelAnimationFrame(clearPeriodBusyRaf1Ref.current);
      clearPeriodBusyRaf1Ref.current = null;
    }
    if (clearPeriodBusyRaf2Ref.current !== null) {
      window.cancelAnimationFrame(clearPeriodBusyRaf2Ref.current);
      clearPeriodBusyRaf2Ref.current = null;
    }
    if (clearPeriodBusyTimerRef.current !== null) {
      window.clearTimeout(clearPeriodBusyTimerRef.current);
      clearPeriodBusyTimerRef.current = null;
    }

    setIsPeriodRenderBusy(false);
  };

  // 「開始＋X分」で終了時刻をクイック設定する。
  const setQuickEndFromStart = (minutes: number) => {
    const startAt = parseDateTimeLocal(startInput);
    if (!startAt) {
      window.alert("開始時刻を正しく設定してください。");
      return;
    }
    const endAt = new Date(startAt.getTime() + minutes * 60_000);
    setEndInput(toDateTimeLocalValue(endAt));
  };

  const quickEndOffsetMinutes = useMemo(() => {
    const startAt = parseDateTimeLocal(startInput);
    const endAt = parseDateTimeLocal(endInput);
    if (!startAt || !endAt) return "";

    const deltaMs = endAt.getTime() - startAt.getTime();
    if (!Number.isFinite(deltaMs) || deltaMs < 0) return "";

    const minutes = deltaMs / 60_000;
    if (!Number.isInteger(minutes)) return "";

    const allowed = QUICK_END_OFFSET_MINUTES_OPTIONS as readonly number[];
    return allowed.includes(minutes) ? String(minutes) : "";
  }, [startInput, endInput]);

  const chartPoints: ChartPoint[] = useMemo(() => {
    const axisEndMs =
      realtimeAxisEndMs ??
      getRealtimeAxisEndMs(
        bpslogDataForDisplay,
        realtimeNowMs,
        REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
        stickyRealtimeAxisEndMsRef.current,
      ).endMs;
    const raw = isRealtime
      ? buildRealtimeChartPoints(
          bpslogDataForDisplay,
          axisEndMs,
          realtimeWindowMinutesForDisplay,
        )
      : (() => {
          const shouldAlignWithApiCap =
            disableMapDownsample &&
            !disableChartDownsample &&
            bpslogDataForDisplay.length > MAX_POINTS_FOR_UI;

          if (!shouldAlignWithApiCap) {
            return buildPeriodChartPoints(
              bpslogDataForDisplay,
              appliedStartInput,
              appliedEndInput,
            );
          }

          const rangeStartMs = parseDateTimeLocal(appliedStartInput)?.getTime();
          const rangeEndMs = parseDateTimeLocal(appliedEndInput)?.getTime();

          const pickedIdxs = pickRepresentativeBpsLogIndicesForRangeLikeApiCap({
            input: bpslogDataForDisplay,
            take: MAX_POINTS_FOR_UI,
            gapThresholdMs: 3_000,
            rangeStartMs:
              typeof rangeStartMs === "number" && Number.isFinite(rangeStartMs)
                ? rangeStartMs
                : null,
            rangeEndMs:
              typeof rangeEndMs === "number" && Number.isFinite(rangeEndMs)
                ? rangeEndMs
                : null,
          });

          const subset = pickedIdxs.map((i) => bpslogDataForDisplay[i]);
          const built = buildPeriodChartPoints(
            subset,
            appliedStartInput,
            appliedEndInput,
          );

          // subset内の originalIdx(0..n-1) を、元配列(bpslogDataForDisplay)の index に戻す
          return built.map((p) => {
            if (p.originalIdx === null) return p;
            const mapped = pickedIdxs[p.originalIdx];
            return {
              ...p,
              originalIdx: typeof mapped === "number" ? mapped : null,
            };
          });
        })();

    // realtimeは1秒バケツの固定点列なので、UI側で間引かずにそのまま表示する。
    if (isRealtime) return raw;

    if (disableChartDownsample) return raw;
    return downsampleChartPoints(raw, MAX_CHART_POINTS);
  }, [
    isRealtime,
    bpslogDataForDisplay,
    realtimeAxisEndMs,
    realtimeNowMs,
    realtimeWindowMinutesForDisplay,
    appliedStartInput,
    appliedEndInput,
    disableChartDownsample,
    disableMapDownsample,
  ]);

  // 期間指定モード中は、取得中フラグに合わせて「更新中」を必ず出す。
  useEffect(() => {
    if (isRealtime) {
      setIsPeriodRenderBusy(false);
      return;
    }
    if (isBpslogRangeLoading) {
      setIsPeriodRenderBusy(true);
    }
  }, [isRealtime, isBpslogRangeLoading]);

  // 期間指定モード中は、取得条件が変わった瞬間に即「更新中」へ倒す（isRangeLoading伝播前の「完了」表示を防ぐ）。
  useEffect(() => {
    if (isRealtime) return;
    if (periodFetchKey === null) return;
    setIsPeriodRenderBusy(true);
  }, [isRealtime, periodFetchKey]);

  // 期間指定モード中は、busy=trueだがloadingが立たないケース（キャッシュヒット等）でも描画が落ち着いてからbusy解除する。
  useEffect(() => {
    if (clearPeriodBusyRaf1Ref.current !== null) {
      window.cancelAnimationFrame(clearPeriodBusyRaf1Ref.current);
      clearPeriodBusyRaf1Ref.current = null;
    }
    if (clearPeriodBusyRaf2Ref.current !== null) {
      window.cancelAnimationFrame(clearPeriodBusyRaf2Ref.current);
      clearPeriodBusyRaf2Ref.current = null;
    }
    if (clearPeriodBusyTimerRef.current !== null) {
      window.clearTimeout(clearPeriodBusyTimerRef.current);
      clearPeriodBusyTimerRef.current = null;
    }

    if (isRealtime) return;
    if (isBpslogRangeLoading) return;
    if (!isPeriodRenderBusy) return;

    // isRangeLoading伝播の隙間で「完了」が出ないよう、busyを最短時間だけ維持してから解除する。
    clearPeriodBusyTimerRef.current = window.setTimeout(() => {
      clearPeriodBusyRaf1Ref.current = window.requestAnimationFrame(() => {
        clearPeriodBusyRaf2Ref.current = window.requestAnimationFrame(() => {
          setIsPeriodRenderBusy(false);
        });
      });
    }, 200);
  }, [isRealtime, isBpslogRangeLoading, isPeriodRenderBusy]);

  useEffect(() => {
    chartPointsRef.current = chartPoints;
    bpslogDataForDisplayRef.current = bpslogDataForDisplay;
  }, [chartPoints, bpslogDataForDisplay]);

  const latestRealChartIdx = isRealtime
    ? Math.max(0, chartPoints.length - 1)
    : (() => {
        for (let i = chartPoints.length - 1; i >= 0; i--) {
          if (chartPoints[i]?.originalIdx !== null) return i;
        }
        return 0;
      })();

  const selectedChartIdx = forceLatest
    ? latestRealChartIdx
    : hoveredIdx !== null
      ? hoveredIdx
      : latestRealChartIdx;

  const selectedOriginalIdx =
    chartPoints[selectedChartIdx]?.originalIdx ?? null;
  const selectedPoint =
    selectedOriginalIdx !== null
      ? (bpslogDataForDisplay[selectedOriginalIdx] ?? null)
      : null;

  const selectedTimestampForHeader = isRealtime
    ? (chartPoints[selectedChartIdx]?.timestamp ?? null)
    : (selectedPoint?.timestamp ?? null);

  const latestGpsPointForMap = useMemo(() => {
    for (let i = bpslogDataForDisplay.length - 1; i >= 0; i--) {
      const pt = bpslogDataForDisplay[i];
      if (pt?.gps?.lat == null || pt?.gps?.lng == null) continue;
      return pt;
    }
    return null;
  }, [bpslogDataForDisplay]);

  const mapPointerPoint = useMemo(() => {
    if (!isRealtime) {
      return selectedPoint;
    }

    if (forceLatest) {
      return latestGpsPointForMap;
    }

    if (!selectedTimestampForHeader) return null;

    const targetMs = new Date(selectedTimestampForHeader).getTime();
    if (!Number.isFinite(targetMs)) return null;

    const bucketEndMs = targetMs + 1000;
    for (let i = bpslogDataForDisplay.length - 1; i >= 0; i--) {
      const pt = bpslogDataForDisplay[i];
      const t = new Date(pt.timestamp).getTime();
      if (!Number.isFinite(t)) break;

      if (t >= bucketEndMs) continue;
      if (t < targetMs) break;

      if (pt?.gps?.lat == null || pt?.gps?.lng == null) continue;
      return pt;
    }

    return null;
  }, [
    bpslogDataForDisplay,
    isRealtime,
    forceLatest,
    latestGpsPointForMap,
    selectedTimestampForHeader,
    selectedPoint,
  ]);

  const selectedPointForNowPanel = useMemo(() => {
    if (!isRealtime) return selectedPoint;
    if (!selectedTimestampForHeader) return null;

    const targetMs = new Date(selectedTimestampForHeader).getTime();
    if (!Number.isFinite(targetMs)) return null;

    const bucketEndMs = targetMs + 1000;
    for (let i = bpslogDataForDisplay.length - 1; i >= 0; i--) {
      const pt = bpslogDataForDisplay[i];
      const t = new Date(pt.timestamp).getTime();
      if (!Number.isFinite(t)) break;

      if (t >= bucketEndMs) continue;
      if (t < targetMs) break;

      return pt;
    }

    return null;
  }, [
    bpslogDataForDisplay,
    isRealtime,
    selectedPoint,
    selectedTimestampForHeader,
  ]);

  const shouldShowSelectedTime = Boolean(
    selectedTimestampForHeader &&
    (isRealtime ? selectedPointForNowPanel : selectedPoint),
  );

  const showDateOnXTicks = useMemo(() => {
    if (isRealtime) return false;
    const s = parseDateTimeLocal(appliedStartInput);
    const e = parseDateTimeLocal(appliedEndInput);
    if (!s || !e) return false;
    const span = Math.abs(e.getTime() - s.getTime());
    return Number.isFinite(span) ? span >= 6 * 60 * 60 * 1000 : false;
  }, [isRealtime, appliedStartInput, appliedEndInput]);

  const periodAxis = useMemo(() => {
    if (isRealtime) return null;
    const s = parseDateTimeLocal(appliedStartInput);
    const e = parseDateTimeLocal(appliedEndInput);
    if (!s || !e) return null;
    return { startMs: s.getTime(), endMs: e.getTime() };
  }, [isRealtime, appliedStartInput, appliedEndInput]);

  const [axisMeta, setAxisMeta] = useState<{
    spanMs: number | null;
    tickMs: number | null;
  }>({ spanMs: null, tickMs: null });

  const updateAxisMetaFromChart = useMemo(() => {
    return (chart: any) => {
      const scale = chart?.scales?.x;
      const minRaw = scale?.min;
      const maxRaw = scale?.max;
      const min = Number(minRaw);
      const max = Number(maxRaw);
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        setAxisMeta({ spanMs: null, tickMs: null });
        return;
      }

      const ticksRaw: any[] = Array.isArray(scale?.ticks) ? scale.ticks : [];
      const tickValues = ticksRaw
        .map((t) => {
          if (typeof t === "number") return t;
          const v = (t as any)?.value;
          return typeof v === "number" ? v : Number(v);
        })
        .filter((v) => Number.isFinite(v))
        .sort((a, b) => a - b);

      const diffs: number[] = [];
      for (let i = 0; i + 1 < tickValues.length; i++) {
        const d = tickValues[i + 1] - tickValues[i];
        if (Number.isFinite(d) && d > 0) diffs.push(d);
      }
      diffs.sort((a, b) => a - b);
      const tickMs = diffs.length ? diffs[Math.floor(diffs.length / 2)] : null;

      setAxisMeta({ spanMs: Math.abs(max - min), tickMs });
    };
  }, []);

  const chartsMeta = useMemo(() => {
    const selectedText = shouldShowSelectedTime
      ? formatTimeLabel(selectedTimestampForHeader ?? "", true, true)
      : "--";

    const spanMsForDisplay = (() => {
      if (!isRealtime) return axisMeta.spanMs;
      if (
        typeof axisMeta.spanMs === "number" &&
        Number.isFinite(axisMeta.spanMs)
      ) {
        // realtime内部は(windowSec-1)sでbucketずれを避けるが、表示上は設定値(例:10:00)に合わせる。
        return axisMeta.spanMs + 1000;
      }
      return realtimeWindowMinutesForDisplay * 60_000;
    })();

    const widthText =
      typeof spanMsForDisplay === "number" && Number.isFinite(spanMsForDisplay)
        ? formatDurationJa(spanMsForDisplay)
        : "--";
    return { selectedText, widthText };
  }, [
    shouldShowSelectedTime,
    selectedTimestampForHeader,
    axisMeta.spanMs,
    isRealtime,
    realtimeWindowMinutesForDisplay,
  ]);

  const stepSize = 0.5;
  let bitrateMax = 0;
  let hasAnyBitrate = false;
  for (let i = 0; i < chartPoints.length; i++) {
    const p = chartPoints[i];
    const vals = [p.l1, p.l2, p.l3, p.l4, p.actual];
    for (let j = 0; j < vals.length; j++) {
      const v = vals[j];
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      if (!hasAnyBitrate) {
        bitrateMax = v;
        hasAnyBitrate = true;
        continue;
      }
      if (v > bitrateMax) bitrateMax = v;
    }
  }
  if (!hasAnyBitrate) bitrateMax = 0;
  const bitrateYMax = Math.max(
    4,
    Math.ceil((bitrateMax + 0.001) / stepSize) * stepSize,
  );

  const selectedXMsForPlugins = useMemo(() => {
    if (!selectedTimestampForHeader) return null;
    const ms = chartPoints[selectedChartIdx]?.xMs;
    return typeof ms === "number" && Number.isFinite(ms) ? ms : null;
  }, [selectedTimestampForHeader, chartPoints, selectedChartIdx]);

  useBpsLogChartSelectionSync({
    chartRefs,
    chartConfigs,
    chartPoints,
    selectedChartIdx,
    selectedXMsForPlugins,
    getSeriesY,
  });

  const { handleZoomOrPanComplete, handlePeriodWheel, handleRealtimeWheel } =
    useBpsLogChartZoom({
      isRealtime,
      isRealtimeRef,
      realtimeWindowMinutes,
      selectedTimestampForHeader,
      leftStackRef,
      chartRefs,
      chartPointsRef,
      bpslogDataForDisplayRef,
      appliedRangeRef,
      appliedStartInput,
      appliedEndInput,
      startInput,
      endInput,
      setIsRealtime: setMonitorMode,
      setStartInput,
      setEndInput,
      setAppliedStartInput,
      setAppliedEndInput,
    });

  const handleZoomOrPanCompleteWithMeta = useMemo(() => {
    return (chart: any) => {
      updateAxisMetaFromChart(chart);
      handleZoomOrPanComplete(chart);
    };
  }, [updateAxisMetaFromChart, handleZoomOrPanComplete]);

  const { bitrateChartOptions, chartDataList } = useBpsLogBitrateCharts({
    isRealtime,
    realtimeWindowMinutes: realtimeWindowMinutesForDisplay,
    realtimeAxisEndMs,
    realtimeNowMs,
    realtimeRightEdgeFallbackLagMs: REALTIME_RIGHT_EDGE_FALLBACK_LAG_MS,
    periodAxis,
    showDateOnXTicks,
    bitrateYMax,
    stepSize,
    handleZoomOrPanComplete: handleZoomOrPanCompleteWithMeta,
    forceLatestRef,
    pendingHoverIdxRef,
    hoverRafRef,
    suppressHoverUntilMsRef,
    setHoveredIdx,
    chartConfigs,
    chartPoints,
    getSeriesY,
  });

  // 実際の描画結果に合わせてメタ情報（表示幅/目盛間隔）を同期する。
  useEffect(() => {
    const chart = chartRefs.current?.[0];
    if (!chart) return;
    const raf = window.requestAnimationFrame(() => {
      updateAxisMetaFromChart(chart);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    updateAxisMetaFromChart,
    chartPoints,
    isRealtime,
    realtimeWindowMinutesForDisplay,
    appliedStartInput,
    appliedEndInput,
    realtimeAxisEndMs,
    realtimeNowMs,
  ]);

  const chartRefSetters = useMemo(() => {
    return chartConfigs.map((_, idx) => {
      return (chart: any) => {
        chartRefs.current[idx] = chart;
      };
    });
  }, [chartConfigs]);

  const wheelHandlersNative = useMemo(() => {
    return chartConfigs.map((_, idx) => {
      return (e: WheelEvent) => {
        const now =
          typeof performance !== "undefined" &&
          typeof performance.now === "function"
            ? performance.now()
            : Date.now();
        suppressHoverUntilMsRef.current = now + 220;
        return isRealtimeRef.current
          ? handleRealtimeWheel(e, idx)
          : handlePeriodWheel(e, idx);
      };
    });
  }, [chartConfigs, handlePeriodWheel, handleRealtimeWheel, isRealtimeRef]);

  const displayPointForNowPanel = isRealtime
    ? forceLatest
      ? (bpslogDataForDisplay[bpslogDataForDisplay.length - 1] ?? null)
      : selectedPointForNowPanel
    : selectedPoint;

  const latestActualBps: number | null =
    displayPointForNowPanel &&
    typeof displayPointForNowPanel.actual_bitrate === "number"
      ? displayPointForNowPanel.actual_bitrate
      : null;
  const latestFrameRate: number | null =
    displayPointForNowPanel &&
    typeof displayPointForNowPanel.frame_rate === "number"
      ? displayPointForNowPanel.frame_rate
      : null;
  const latestDelayTime: number | null =
    displayPointForNowPanel &&
    typeof displayPointForNowPanel.delay_time === "number"
      ? displayPointForNowPanel.delay_time
      : null;

  return (
    <div className="bpslogScreen">
      <BpsLogHeader
        logoSrc={logo}
        vehicleId={vehicleId}
        vehicleOptions={vehicleOptions}
        onVehicleChange={setVehicleId}
        isRealtime={isRealtime}
        setMonitorMode={setMonitorMode}
        realtimeWindowMinutes={realtimeWindowMinutes}
        onRealtimeWindowMinutesChange={setRealtimeWindowMinutes}
        startInput={startInput}
        endInput={endInput}
        onStartInputChange={setStartInput}
        onEndInputChange={setEndInput}
        quickEndOffsetMinutes={quickEndOffsetMinutes}
        onQuickEndOffsetMinutesChange={setQuickEndFromStart}
        quickEndOffsetMinutesOptions={QUICK_END_OFFSET_MINUTES_OPTIONS}
        canApplyPeriodRange={canApplyPeriodRange}
        onApplyPeriodRange={applyPeriodRange}
        onCancelPeriodRange={cancelPeriodRange}
        isBpslogRangeLoading={isBpslogRangeLoading}
        isPeriodRenderBusy={isPeriodRenderBusy}
        forceLatest={forceLatest}
        onForceLatestChange={setForceLatest}
        chartsMeta={chartsMeta}
        onOpenDownload={openDownloadModal}
        onOpenSettings={() => setIsSettingsModalOpen(true)}
        onOpenMap={() => {
          const now = new Date();
          const start = isRealtime
            ? toDateTimeLocalValue(
                new Date(
                  now.getTime() - realtimeWindowMinutesForDisplay * 60_000,
                ),
              )
            : appliedStartInput;
          const end = isRealtime ? toDateTimeLocalValue(now) : appliedEndInput;
          navigate("/bpslog/map", {
            state: {
              vehicleId,
              startInput: start,
              endInput: end,
            },
          });
        }}
      />

      <SettingsModal
        isOpen={isSettingsModalOpen}
        initialDelayOffsetMs={delayOffsetMs}
        initialCharts={chartConfigs}
        initialFollowMap={followMap}
        initialShowVideo={showVideo}
        initialDisableChartDownsample={disableChartDownsample}
        initialDisableMapDownsample={disableMapDownsample}
        initialMapTileLayer={mapTileLayer}
        initialMapActualBitrateColoring={mapActualBitrateColoring}
        defaultDelayOffsetMs={DEFAULT_DELAY_OFFSET_MS}
        defaultCharts={DEFAULT_CHARTS}
        defaultFollowMap={DEFAULT_FOLLOW_MAP}
        defaultShowVideo={DEFAULT_SHOW_VIDEO}
        defaultDisableChartDownsample={DEFAULT_DISABLE_CHART_DOWNSAMPLE}
        defaultDisableMapDownsample={DEFAULT_DISABLE_MAP_DOWNSAMPLE}
        defaultMapTileLayer={DEFAULT_MAP_TILE_LAYER}
        defaultMapActualBitrateColoring={DEFAULT_MAP_ACTUAL_BITRATE_COLORING}
        onApply={applySettings}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      <DownloadBpsLogModal
        isOpen={isDownloadModalOpen}
        initialVehicleId={downloadInitialVehicleId}
        initialStart={downloadInitialStart}
        initialEnd={downloadInitialEnd}
        delayOffsetMs={delayOffsetMs}
        vehicleOptions={vehicleOptions}
        onClose={() => setIsDownloadModalOpen(false)}
      />

      <div className="bpslogGrid">
        <div
          className="bpslogLeftStack"
          aria-label="bitrateグラフ"
          ref={leftStackRef}
        >
          {chartConfigs.map((cfg, idx) => {
            const data = chartDataList[idx];
            const p = chartPoints[selectedChartIdx];
            const selectedVal = p ? getSeriesY(p, cfg.key) : null;
            return (
              <div key={idx} className="bpslogCell bpslogCell--compact">
                <div className="bpslogChartHeader">
                  <h3>{cfg.name}</h3>
                  <div
                    className="bpslogChartHeaderValue"
                    aria-label="選択中ビットレート"
                  >
                    {typeof selectedVal === "number" &&
                    Number.isFinite(selectedVal)
                      ? `${selectedVal.toFixed(2)} Mbps`
                      : "--"}
                  </div>
                </div>
                <BitrateChartCanvas
                  data={data}
                  options={bitrateChartOptions}
                  onWheel={wheelHandlersNative[idx]}
                  setChartRef={chartRefSetters[idx]}
                />
              </div>
            );
          })}
        </div>

        <div className="bpslogRightStack" aria-label="地図と現在値">
          {showVideo ? (
            <div
              className="bpslogCell bpslogCell--videoFrame"
              aria-label="車両映像"
            >
              <BpsLogVideo
                baseId={baseId}
                vehicleId={vehicleId}
                zaoSuid={selectedZaoSuid}
                isVisible={showVideo}
                fallbackLogoSrc={logo}
              />
            </div>
          ) : null}
          <div
            className={`bpslogCell bpslogCell--mapFrame ${showVideo ? "isSmall" : ""}`}
            aria-label="地図"
          >
            <BpsLogMap
              basePoint={basePoint}
              baseName={baseName}
              coursePoints={coursePoints}
              bpslogData={bpslogDataForMap}
              pointerPoint={mapPointerPoint}
              followPointer={followMap}
              mapTileLayer={mapTileLayer}
              mapActualBitrateColoring={mapActualBitrateColoring}
              vehicleId={vehicleId}
              isRealtime={isRealtime}
              startInput={appliedStartInput}
              endInput={appliedEndInput}
              realtimeWindowMinutes={realtimeWindowMinutesForDisplay}
            />
          </div>

          <BpsLogNowPanel
            latestActualBps={latestActualBps}
            latestDelayTime={latestDelayTime}
            latestFrameRate={latestFrameRate}
          />
        </div>
      </div>
    </div>
  );
};

export default BpsLog;
